import {
  Flame,
  Users,
  GaugeCircle,
  TrendingUp,
  Workflow,
  HeartHandshake,
} from "lucide-react";

const WhyChooseUs = () => {
  const features = [
    {
      Icon: Flame,
      title: "Reduce Burnout",
      description:
        "Offload repetitive, high-fatigue tasks to AI and robotics so your team can focus on customers, sales, and the work that matters.",
    },
    {
      Icon: Users,
      title: "Solve Labor Shortages",
      description:
        "Fill staffing gaps with always-on AI employees and PUDU robots — no callouts, no overtime, no churn.",
    },
    {
      Icon: GaugeCircle,
      title: "Improve Consistency",
      description:
        "Every shift, every customer, every workflow — executed the same way, every time, with measurable quality.",
    },
    {
      Icon: TrendingUp,
      title: "Scale Smarter",
      description:
        "Add capacity without adding headcount. Grow operations, locations, and revenue without the staffing tax.",
    },
    {
      Icon: Workflow,
      title: "Operational Efficiency",
      description:
        "Automate intake, follow-up, scheduling, dispatch, and reporting end-to-end through one integrated AI stack.",
    },
    {
      Icon: HeartHandshake,
      title: "Empower Employees",
      description:
        "We don't replace people — we equip them with AI co-workers that remove pressure and unlock their best work.",
    },
  ];

  return (
    <section className="py-20">
      <div className="text-center mb-14 max-w-3xl mx-auto px-4">
        <p className="text-xs md:text-sm font-bold tracking-[0.25em] text-primary mb-3">
          WHY NEIL AI
        </p>
        <h2 className="font-orbitron font-bold text-3xl md:text-5xl mb-4 text-foreground leading-tight">
          Built For Businesses{" "}
          <span className="text-primary">Under Pressure</span>
        </h2>
        <p className="text-base md:text-lg text-muted-foreground">
          AI automation and robotics that solve real operational problems — labor, burnout, consistency, cost.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {features.map(({ Icon, title, description }) => (
          <div
            key={title}
            className="group relative p-7 rounded-2xl border border-[hsl(var(--line))] glass-effect hover:border-primary/50 hover:-translate-y-1 transition-all duration-500"
            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.35)" }}
          >
            {/* Top gradient accent */}
            <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-70" />

            <div className="mb-5 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 text-primary group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300">
              <Icon className="w-6 h-6" />
            </div>

            <h3 className="font-orbitron font-bold text-lg md:text-xl mb-2 text-foreground">
              {title}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {description}
            </p>

            {/* Hover glow */}
            <div
              className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  "radial-gradient(circle at 30% 0%, hsl(var(--primary) / 0.15), transparent 60%)",
              }}
            />
          </div>
        ))}
      </div>

      <div className="text-center mt-12 pt-8 border-t border-[hsl(var(--line))]">
        <p className="text-sm md:text-base text-muted-foreground max-w-4xl mx-auto leading-relaxed">
          <span className="font-bold text-secondary">Authorized PUDU Robotics Dealer</span>
          <span className="mx-3 text-muted-foreground/40">·</span>
          <span className="font-bold text-primary">U.S.-Based Support &amp; Training</span>
        </p>
      </div>
    </section>
  );
};

export default WhyChooseUs;
